import { useEffect, useRef } from "react";
import { useLocation, useNavigate, Location as ReactRouterLocation } from "react-router-dom";

export type NavigationGuard = (
  to: ReactRouterLocation,
  from: ReactRouterLocation | null,
  next: (path?: string | { path: string; replace?: boolean }) => void
) => void;

interface RouteProtectorProps {
  children: React.ReactNode;
  beforeEach?: NavigationGuard;
  afterEach?: (to: ReactRouterLocation, from: ReactRouterLocation | null) => void;
}

export const RouteProtector = ({ children, beforeEach, afterEach }: RouteProtectorProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const prevLocationRef = useRef<ReactRouterLocation | null>(null);
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const executeGuards = async () => {
      if (beforeEach) {
        const next = async (options?: string | { path: string; replace?: boolean }) => {
          if (!options) return;
          if (typeof options === "string") {
            if (options !== location.pathname) {
              navigate(options, { replace: true });
            }
          } else {
            if (options.path !== location.pathname) {
              navigate(options.path, { replace: options.replace });
            }
          }
        };
        await beforeEach(location, prevLocationRef.current, next);
      }
      if (afterEach && prevLocationRef.current) {
        afterEach(location, prevLocationRef.current);
      }
    };
    executeGuards();
    prevLocationRef.current = location;
  }, [location, navigate, beforeEach, afterEach]);

  return <>{children}</>;
};
